import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Checkbox,
  ckConfirm,
  Field,
  OptionChips,
  Panel,
  Segmented,
  Spinner,
  Stepper,
  Switch,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  mailingApi,
  type AudienceStats,
  type BlastDto,
  type BlastInput,
  type MailingAudience,
  type MailingDesign,
  type MailingSettingsDto,
} from "../../api/mailingApi";
import { useShell } from "../shell/ShellContext";
import { EmailBuilder, MergeTagMenu } from "./EmailBuilder";
import { emptyDesign, errorMessage, fmtNum, toLocalInput } from "./mailingUi";

type Step = "content" | "audience" | "send";

const STATUS_OPTIONS = [
  { value: "active", label: "פעילים" },
  { value: "frozen", label: "מוקפאים" },
  { value: "cancelled", label: "מבוטלים" },
];

function AudienceStatsView({ stats, sample, loading }: { stats: AudienceStats | null; sample: Array<{ email: string; name: string }>; loading: boolean }) {
  if (!stats) return <div className="ml-busy">{loading ? <Spinner size={20} /> : null}</div>;
  const rows: Array<[string, number, string?]> = [
    ["רשומות שנמצאו", stats.contacts],
    ["כתובות מייל", stats.withEmail],
    ["כתובות לא תקינות", stats.invalid, "warn"],
    ["כפולות (יישלח פעם אחת)", stats.duplicates],
    ["חסומות / הוסרו", stats.suppressed, "warn"],
    ["ללא הסכמה", stats.notConsented, "warn"],
  ];
  return (
    <div className="ml-aud">
      <div className="ml-aud__final">
        <span>יקבלו את המייל</span>
        <b>{fmtNum(stats.final)}</b>
        {loading ? <Spinner size={14} /> : null}
      </div>
      <ul className="ml-aud__rows">
        {rows
          .filter(([, n, kind]) => !kind || n > 0)
          .map(([label, n, kind]) => (
            <li key={label} className={kind ? "is-warn" : ""}>
              <span>{label}</span>
              <span>{fmtNum(n)}</span>
            </li>
          ))}
      </ul>
      {sample.length ? (
        <div className="ml-aud__sample">
          <small>לדוגמה:</small>
          {sample.map((s) => (
            <div key={s.email} className="ml-aud__person">
              <span>{s.name || "—"}</span>
              <span dir="ltr">{s.email}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Edit a draft blast: content → audience → send (test, A/B, schedule). Welcome emails: content only. */
export function BlastComposer({ blast: initial, onLaunched }: { blast: BlastDto; onLaunched: (b: BlastDto) => void }) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const canSend = allowed("mailing.send");
  const isWelcome = initial.kind === "welcome";
  const [step, setStep] = useState<Step>("content");
  const [b, setB] = useState<BlastDto>(initial);
  const [design, setDesign] = useState<MailingDesign>(initial.design?.blocks?.length ? (initial.design as MailingDesign) : emptyDesign());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [settings, setSettings] = useState<MailingSettingsDto | null>(null);
  const [preview, setPreview] = useState<{ stats: AudienceStats; sample: Array<{ email: string; name: string }> } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [manualText, setManualText] = useState(() => (initial.audience.entries ?? []).map((e) => (e.name ? `${e.name}, ${e.email}` : e.email)).join("\n"));
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [when, setWhen] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt] = useState(() => toLocalInput(new Date(Date.now() + 3600_000)));
  const [launching, setLaunching] = useState(false);
  const readOnly = !canManage;

  useEffect(() => {
    mailingApi.settings().then(setSettings).catch(() => undefined);
  }, []);

  // bumped on every edit — a save only clears "dirty" if nothing changed while it was in flight
  const version = useRef(0);
  const edit = (patch: Partial<BlastDto>) => {
    setB((prev) => ({ ...prev, ...patch }));
    version.current += 1;
    setDirty(true);
  };
  const editDesign = (d: MailingDesign) => {
    setDesign(d);
    version.current += 1;
    setDirty(true);
  };
  const audience = b.audience;
  const setAudience = (patch: Partial<MailingAudience>) => edit({ audience: { ...audience, ...patch } });

  const payload = useCallback(
    (): BlastInput => ({
      name: b.name,
      subject: b.subject ?? "",
      preheader: b.preheader ?? "",
      fromName: b.fromName ?? "",
      replyTo: b.replyTo ?? "",
      isAdvertising: b.isAdvertising,
      design,
      ...(isWelcome ? {} : { audience: b.audience, ab: { enabled: b.ab.enabled, subjectB: b.ab.subjectB, testPercent: b.ab.testPercent, waitMinutes: b.ab.waitMinutes, metric: b.ab.metric } }),
    }),
    [b, design, isWelcome],
  );

  const save = useCallback(
    async (quiet = true) => {
      if (readOnly) return true;
      setSaving(true);
      const at = version.current;
      try {
        const saved = await mailingApi.blasts.update(b.id, payload());
        setB((prev) => ({ ...prev, html: saved.html, updatedAt: saved.updatedAt }));
        if (version.current === at) setDirty(false);
        setSavedAt(new Date());
        if (!quiet) toast.success("נשמר");
        return true;
      } catch (e) {
        toast.error(errorMessage(e, "השמירה נכשלה"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [b.id, payload, readOnly, toast],
  );

  // autosave a second after the last change
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const t = window.setTimeout(() => void saveRef.current(true), 1200);
    return () => window.clearTimeout(t);
  }, [dirty, b, design, readOnly]);

  // live audience count
  const audienceKey = JSON.stringify(audience);
  useEffect(() => {
    if (isWelcome || step === "content") return undefined;
    setPreviewing(true);
    setPreviewError(null);
    const t = window.setTimeout(() => {
      mailingApi
        .previewAudience(audience)
        .then(setPreview)
        .catch((e) => setPreviewError(errorMessage(e, "לא הצלחנו לחשב את הקהל")))
        .finally(() => setPreviewing(false));
    }, 400);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceKey, step, isWelcome]);

  const statuses = useMemo(() => {
    const raw = audience.filters?.status;
    return Array.isArray(raw) ? raw.map(String) : typeof raw === "string" && raw ? raw.split(",") : [];
  }, [audience.filters]);
  const setFilter = (key: string, value: unknown) => {
    const filters = { ...(audience.filters ?? {}) };
    if (value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length)) delete filters[key];
    else filters[key] = value;
    setAudience({ filters });
  };
  const emailFields = audience.emailFields ?? ["customer", "contact"];
  const toggleEmailField = (f: "customer" | "contact", on: boolean) => {
    const next = on ? Array.from(new Set([...emailFields, f])) : emailFields.filter((x) => x !== f);
    setAudience({ emailFields: next.length ? next : [f === "customer" ? "contact" : "customer"] });
  };

  const applyManual = async () => {
    try {
      const parsed = await mailingApi.parseList(manualText);
      setAudience({ entries: parsed.entries });
    } catch (e) {
      toast.error(errorMessage(e, "לא הצלחנו לקרוא את הרשימה"));
    }
  };

  const goto = async (next: Step) => {
    if (dirty && !(await save(true))) return;
    setStep(next);
  };

  const sendTest = async () => {
    const emails = testTo.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!emails.length) return;
    if (dirty && !(await save(true))) return;
    setTesting(true);
    try {
      const res = await mailingApi.blasts.test(b.id, emails);
      const failed = res.results.filter((r) => !r.ok);
      if (failed.length) toast.error(`לא נשלח ל־${failed.map((f) => f.email).join(", ")}: ${failed[0]?.error ?? ""}`);
      else toast.success(`מייל בדיקה נשלח ל־${res.results.length} כתובות`);
    } catch (e) {
      toast.error(errorMessage(e, "שליחת הבדיקה נכשלה"));
    } finally {
      setTesting(false);
    }
  };

  const launch = async () => {
    if (dirty && !(await save(true))) return;
    const n = preview?.stats.final ?? 0;
    const at = when === "later" ? new Date(scheduledAt) : null;
    if (at && Number.isNaN(at.getTime())) {
      toast.error("מועד התזמון לא תקין");
      return;
    }
    const ok = await ckConfirm({
      title: at ? "לתזמן את הדיוור?" : "לשלוח עכשיו?",
      message: at
        ? `הדיוור "${b.name}" יישלח ל־${fmtNum(n)} נמענים ב־${at.toLocaleString("he-IL")}. עד אז אפשר לבטל את התזמון.`
        : `הדיוור "${b.name}" יישלח עכשיו ל־${fmtNum(n)} נמענים. אחרי שהשליחה מתחילה אפשר להשהות או לעצור, אבל לא לבטל מה שכבר נשלח.`,
      confirmLabel: at ? "תזמון" : "שליחה",
    });
    if (!ok) return;
    setLaunching(true);
    try {
      const res = await mailingApi.blasts.launch(b.id, at ? at.toISOString() : null);
      toast.success(at ? "הדיוור תוזמן" : `השליחה התחילה — ${fmtNum(res.stats.final)} נמענים`);
      onLaunched(res.blast);
    } catch (e) {
      toast.error(errorMessage(e, "השליחה נכשלה"));
    } finally {
      setLaunching(false);
    }
  };

  const footerMissing = b.isAdvertising && settings && !settings.footerAddress;
  const steps = isWelcome
    ? [{ key: "content", label: "תוכן" }]
    : [
        { key: "content", label: "תוכן" },
        { key: "audience", label: "קהל" },
        { key: "send", label: "שליחה" },
      ];

  return (
    <div className="ml-composer">
      <div className="ml-composer__bar">
        <Stepper steps={steps} active={step} />
        <div className="ml-row">
          <small className="ml-muted">{saving ? "שומר…" : dirty ? "שינויים לא שמורים" : savedAt ? "נשמר" : ""}</small>
          {canManage ? (
            <Button size="sm" icon="check" onClick={() => void save(false)} disabled={saving || !dirty}>
              שמירה
            </Button>
          ) : null}
          {isWelcome ? (
            <Button size="sm" variant="ghost" onClick={() => navigate("/mailing?tab=forms")}>
              חזרה לטפסים
            </Button>
          ) : null}
        </div>
      </div>

      {step === "content" ? (
        <div className="ml-stack">
          <Panel title="פרטי המייל" icon="mail">
            <div className="ml-grid2">
              <Field label="שם הדיוור (פנימי)">
                <TextInput value={b.name} onChange={(e) => edit({ name: e.target.value })} disabled={readOnly} maxLength={200} />
              </Field>
              <Field label="נושא" hint={b.isAdvertising ? 'יתווסף "פרסומת:" בתחילת הנושא' : undefined}>
                <div className="ml-with-menu">
                  <TextInput value={b.subject ?? ""} onChange={(e) => edit({ subject: e.target.value })} disabled={readOnly} maxLength={300} />
                  <MergeTagMenu onPick={(t) => edit({ subject: `${b.subject ?? ""}${t}` })} disabled={readOnly} />
                </div>
              </Field>
              <Field label="טקסט תצוגה מקדימה" hint="השורה שמופיעה ליד הנושא בתיבת הדואר">
                <TextInput value={b.preheader ?? ""} onChange={(e) => edit({ preheader: e.target.value })} disabled={readOnly} maxLength={300} />
              </Field>
              <Field label="שם השולח" hint={settings ? `ברירת מחדל: ${settings.senderName}` : undefined}>
                <TextInput value={b.fromName ?? ""} onChange={(e) => edit({ fromName: e.target.value })} disabled={readOnly} maxLength={120} placeholder={settings?.senderName} />
              </Field>
              <Field label="כתובת למענה" hint={settings?.replyTo ? `ברירת מחדל: ${settings.replyTo}` : "תשובות לנמענים יגיעו לכאן"}>
                <TextInput ltr type="email" value={b.replyTo ?? ""} onChange={(e) => edit({ replyTo: e.target.value })} disabled={readOnly} placeholder={settings?.replyTo || undefined} />
              </Field>
              <div className="ml-switch-cell">
                <Switch
                  checked={b.isAdvertising}
                  onChange={(v) => edit({ isAdvertising: v })}
                  disabled={readOnly}
                  label="דבר פרסומת"
                  hint="כולל בקשת תרומה או קידום. חוק התקשורת מחייב סימון וכתובת הארגון."
                />
              </div>
            </div>
            {footerMissing ? (
              <Banner tone="warning" action={<Button size="sm" onClick={() => navigate("/mailing?tab=settings")}>להגדרות</Button>}>
                לדבר פרסומת חובה למלא את כתובת הארגון בהגדרות הדיוור — בלעדיה אי אפשר לשלוח.
              </Banner>
            ) : null}
          </Panel>
          <EmailBuilder design={design} onChange={editDesign} disabled={readOnly} />
          {!isWelcome ? (
            <div className="ml-row ml-row--end">
              <Button variant="primary" icon="left" onClick={() => void goto("audience")}>
                המשך לבחירת קהל
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {step === "audience" ? (
        <div className="ml-split">
          <Panel title="למי לשלוח" icon="users">
            <div className="ml-stack">
              <Segmented
                ariaLabel="מקור הנמענים"
                value={audience.source ?? "customers"}
                onChange={(v) => setAudience({ source: v as MailingAudience["source"] })}
                items={[
                  { value: "customers", label: "לקוחות" },
                  { value: "subscribers", label: "מנויים" },
                  { value: "manual", label: "רשימה ידנית" },
                ]}
              />
              {(audience.source ?? "customers") === "customers" ? (
                <>
                  {audience.customerIds?.length ? (
                    <Banner tone="info" action={<Button size="sm" onClick={() => setAudience({ customerIds: undefined })}>ניקוי הבחירה</Button>}>
                      נבחרו {fmtNum(audience.customerIds.length)} לקוחות מהרשימה.
                    </Banner>
                  ) : null}
                  <Field label="סטטוס">
                    <div className="ml-row">
                      {STATUS_OPTIONS.map((o) => (
                        <Checkbox
                          key={o.value}
                          label={o.label}
                          checked={statuses.includes(o.value)}
                          disabled={readOnly}
                          onChange={(on) => setFilter("status", on ? [...statuses, o.value] : statuses.filter((s) => s !== o.value))}
                        />
                      ))}
                    </div>
                  </Field>
                  <div className="ml-row">
                    <Checkbox label="רק עם חוב פתוח" checked={audience.filters?.flagDebt === true} disabled={readOnly} onChange={(on) => setFilter("flagDebt", on ? true : undefined)} />
                    <Checkbox label="רק בסיכון" checked={audience.filters?.flagAtRisk === true} disabled={readOnly} onChange={(on) => setFilter("flagAtRisk", on ? true : undefined)} />
                  </div>
                  <Field label="חיפוש" hint="אותו חיפוש של רשימת הלקוחות (שם, עיר, קהילה, טלפון…)">
                    <TextInput value={audience.q ?? ""} onChange={(e) => setAudience({ q: e.target.value })} disabled={readOnly} />
                  </Field>
                  <Field label="לאיזו כתובת">
                    <div className="ml-row">
                      <Checkbox label="מייל הלקוח" checked={emailFields.includes("customer")} disabled={readOnly} onChange={(on) => toggleEmailField("customer", on)} />
                      <Checkbox label="מייל איש הקשר הראשי" checked={emailFields.includes("contact")} disabled={readOnly} onChange={(on) => toggleEmailField("contact", on)} />
                    </div>
                  </Field>
                </>
              ) : null}
              {audience.source === "subscribers" ? (
                <p className="ml-note">כל מי שנרשם בטופס הרשמה ואישר, או שהוספתם כמנוי עם הסכמה. ניהול בלשונית "נמענים".</p>
              ) : null}
              {audience.source === "manual" ? (
                <Field label="כתובות" hint='כתובת בכל שורה, אפשר עם שם: "דוד כהן, david@example.org"' full>
                  <TextArea rows={8} dir="auto" value={manualText} onChange={(e) => setManualText(e.target.value)} onBlur={() => void applyManual()} disabled={readOnly} />
                </Field>
              ) : null}
              <Switch
                checked={audience.consentMode === "consented"}
                onChange={(v) => setAudience({ consentMode: v ? "consented" : "all" })}
                disabled={readOnly}
                label="רק מי שהסכים לקבל דיוור"
                hint="שולח רק לכתובות שיש להן הסכמה (מנויים). מומלץ לדבר פרסומת."
              />
            </div>
          </Panel>
          <Panel title="סיכום הקהל" icon="chart">
            {previewError ? <Banner tone="danger">{previewError}</Banner> : <AudienceStatsView stats={preview?.stats ?? null} sample={preview?.sample ?? []} loading={previewing} />}
          </Panel>
          <div className="ml-row ml-row--between ml-split__nav">
            <Button icon="right" onClick={() => void goto("content")}>
              חזרה לתוכן
            </Button>
            <Button variant="primary" icon="left" onClick={() => void goto("send")}>
              המשך לשליחה
            </Button>
          </div>
        </div>
      ) : null}

      {step === "send" ? (
        <div className="ml-split">
          <div className="ml-stack">
            <Panel title="מייל בדיקה" icon="mail" sub="נשלח עם '[בדיקה]' בנושא, עם הנתונים של הנמען הראשון">
              <div className="ml-with-menu">
                <TextInput ltr placeholder="me@example.org" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
                <Button icon="play" onClick={() => void sendTest()} disabled={testing || !testTo.trim() || !canManage}>
                  {testing ? "שולח…" : "שליחת בדיקה"}
                </Button>
              </div>
            </Panel>

            <Panel title="בדיקת A/B על הנושא" icon="sliders">
              <div className="ml-stack">
                <Switch
                  checked={b.ab.enabled}
                  onChange={(v) => edit({ ab: { ...b.ab, enabled: v } })}
                  disabled={readOnly}
                  label="לבדוק שני נושאים"
                  hint="חלק מהקהל מקבל נושא A, חלק נושא B; השאר מקבלים את המנצח. דורש לפחות 100 נמענים."
                />
                {b.ab.enabled ? (
                  <div className="ml-grid2">
                    <Field label="נושא B" full>
                      <TextInput value={b.ab.subjectB} onChange={(e) => edit({ ab: { ...b.ab, subjectB: e.target.value } })} disabled={readOnly} maxLength={300} />
                    </Field>
                    <Field label={`גודל קבוצת הבדיקה: ${b.ab.testPercent}%`}>
                      <input type="range" min={10} max={50} step={5} value={b.ab.testPercent} onChange={(e) => edit({ ab: { ...b.ab, testPercent: Number(e.target.value) } })} disabled={readOnly} />
                    </Field>
                    <Field label="המתנה לפני הכרעה">
                      <OptionChips
                        ariaLabel="המתנה"
                        options={[
                          { value: "60", label: "שעה" },
                          { value: "240", label: "4 שעות" },
                          { value: "720", label: "12 שעות" },
                          { value: "1440", label: "יום" },
                        ]}
                        value={String(b.ab.waitMinutes)}
                        onChange={(v) => edit({ ab: { ...b.ab, waitMinutes: Number(v) } })}
                      />
                    </Field>
                    <Field label="המנצח לפי">
                      <OptionChips
                        ariaLabel="מדד"
                        options={[
                          { value: "open", label: "פתיחות" },
                          { value: "click", label: "הקלקות" },
                        ]}
                        value={b.ab.metric}
                        onChange={(v) => edit({ ab: { ...b.ab, metric: v as "open" } })}
                      />
                    </Field>
                  </div>
                ) : null}
              </div>
            </Panel>
          </div>

          <Panel title="שליחה" icon="play">
            <div className="ml-stack">
              <AudienceStatsView stats={preview?.stats ?? null} sample={[]} loading={previewing} />
              <Segmented
                ariaLabel="מתי"
                value={when}
                onChange={(v) => setWhen(v as "now")}
                items={[
                  { value: "now", label: "עכשיו" },
                  { value: "later", label: "בזמן מתוזמן" },
                ]}
              />
              {when === "later" ? (
                <Field label="מועד השליחה">
                  <TextInput ltr type="datetime-local" value={scheduledAt} min={toLocalInput(new Date())} onChange={(e) => setScheduledAt(e.target.value)} />
                </Field>
              ) : null}
              {footerMissing ? <Banner tone="warning">חסרה כתובת הארגון בהגדרות הדיוור (חובה לדבר פרסומת).</Banner> : null}
              {settings?.quota ? (
                <small className="ml-muted">
                  מכסה החודש: נשלחו {fmtNum(settings.quota.used)} מתוך {fmtNum(settings.quota.quota)} · נותרו {fmtNum(settings.quota.remaining)}
                </small>
              ) : null}
              {canSend ? (
                <Button variant="primary" icon={when === "later" ? "clock" : "play"} onClick={() => void launch()} disabled={launching || !preview?.stats.final || Boolean(footerMissing)}>
                  {launching ? "רגע…" : when === "later" ? "תזמון הדיוור" : `שליחה ל־${fmtNum(preview?.stats.final ?? 0)} נמענים`}
                </Button>
              ) : (
                <Banner tone="info">אין לך הרשאה לשלוח דיוור לקהל. אפשר להכין טיוטה ולבקש ממי שמורשה לשלוח.</Banner>
              )}
            </div>
          </Panel>
          <div className="ml-row ml-split__nav">
            <Button icon="right" onClick={() => void goto("audience")}>
              חזרה לקהל
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
